/* Sharkskin site-wide vehicle persistence.
   Reads the payload saved by car-registration-lookup.js (localStorage
   `sharkskin_vehicle`, the full API response) and hydrates:
     - product cards:        [data-sk-product-handle] gets a green fits badge
                             injected into its [data-sk-badges] slot
     - collection bar:       [data-sk-vehicle-bar] toggles its
                             [data-sk-vehicle-known]/[data-sk-vehicle-unknown]
                             children; [data-sk-vehicle-label] gets the label
     - PDP fit check:        [data-sk-fit-check data-sk-product-handle]
                             toggles [data-sk-fit-yes]/[data-sk-fit-no]/
                             [data-sk-fit-unknown] children
     - rego results CTA:     [data-shop-vehicle] href resolved via the JSON
                             map in [data-sk-vehicle-map] (model > make)
   Clearing: any [data-sk-vehicle-change] click clears both storage keys and
   dispatches `vehicle-cleared`. Listens for the bubbling `vehicle-found` /
   `vehicle-cleared` events from the lookup element. Green styling is
   reserved for fitment confirmation. */
(function () {
  'use strict';

  var VEHICLE_KEY = 'sharkskin_vehicle';
  var FORM_KEY = 'sharkskin_form_state';

  function readPayload() {
    try {
      var raw = localStorage.getItem(VEHICLE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      var vehicle = parsed.vehicle || (parsed.make ? parsed : null);
      if (!vehicle || !vehicle.make) return null;
      return { vehicle: vehicle, products: Array.isArray(parsed.products) ? parsed.products : [], fallbackUsed: Boolean(parsed.fallbackUsed) };
    } catch (e) {
      return null;
    }
  }

  function vehicleLabel(vehicle) {
    if (!vehicle) return '';
    return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
  }

  function fitHandles(payload) {
    var set = {};
    if (payload) {
      payload.products.forEach(function (p) {
        if (p && p.handle) set[norm(p.handle)] = true;
      });
    }
    return set;
  }

  function norm(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function readVehicleMap() {
    var node = document.querySelector('[data-sk-vehicle-map]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /* model match first, then make, else null */
  function resolveVehicleUrl(vehicle) {
    var map = readVehicleMap();
    if (!map || !vehicle) return null;
    var model = norm(vehicle.model);
    var make = norm(vehicle.make);
    if (model && map.models) {
      for (var m in map.models) {
        var key = norm(m);
        if (key === model) return map.models[m];
        // prefix match either way ("landcruiser" ↔ "landcruiser300"), guarded for length
        if (key.length > 3 && model.length > 3 && (key.indexOf(model) === 0 || model.indexOf(key) === 0)) return map.models[m];
      }
    }
    if (make && map.makes) {
      for (var k in map.makes) {
        if (norm(k) === make) return map.makes[k];
      }
    }
    return null;
  }

  function productFits(payload, handle, makeAttr, modelAttr) {
    if (!payload) return false;
    var handles = fitHandles(payload);
    if (handle && handles[norm(handle)]) return true;
    // Metafield fallback when the API returned no product list
    if (!payload.products.length && makeAttr && modelAttr) {
      return norm(makeAttr) === norm(payload.vehicle.make) && norm(modelAttr) === norm(payload.vehicle.model);
    }
    return false;
  }

  function hydrateCards(payload) {
    var cards = document.querySelectorAll('[data-sk-product-handle]:not([data-sk-fit-check])');
    Array.prototype.forEach.call(cards, function (card) {
      var slot = card.querySelector('[data-sk-badges]') || card;
      var existing = slot.querySelector('.sk-fits-badge');
      var fits = productFits(payload, card.getAttribute('data-sk-product-handle'), card.getAttribute('data-sk-make'), card.getAttribute('data-sk-model'));
      if (fits && !existing) {
        var badge = document.createElement('span');
        badge.className = 'sk-badge sk-badge-green sk-fits-badge';
        badge.textContent = 'Fits your car';
        slot.insertBefore(badge, slot.firstChild);
      } else if (!fits && existing) {
        existing.remove();
      }
    });
  }

  function hydrateBars(payload) {
    var bars = document.querySelectorAll('[data-sk-vehicle-bar]');
    Array.prototype.forEach.call(bars, function (bar) {
      var known = bar.querySelector('[data-sk-vehicle-known]');
      var unknown = bar.querySelector('[data-sk-vehicle-unknown]');
      if (known) known.classList.toggle('hidden', !payload);
      if (unknown) unknown.classList.toggle('hidden', Boolean(payload));
      if (payload) {
        Array.prototype.forEach.call(bar.querySelectorAll('[data-sk-vehicle-label]'), function (el) {
          el.textContent = vehicleLabel(payload.vehicle);
        });
      }
    });
  }

  function hydrateFitChecks(payload) {
    var checks = document.querySelectorAll('[data-sk-fit-check]');
    Array.prototype.forEach.call(checks, function (fc) {
      var yes = fc.querySelector('[data-sk-fit-yes]');
      var no = fc.querySelector('[data-sk-fit-no]');
      var unknown = fc.querySelector('[data-sk-fit-unknown]');
      var state = 'unknown';
      if (payload) {
        state = productFits(payload, fc.getAttribute('data-sk-product-handle'), fc.getAttribute('data-sk-make'), fc.getAttribute('data-sk-model')) ? 'yes' : 'no';
      }
      if (yes) yes.classList.toggle('hidden', state !== 'yes');
      if (no) no.classList.toggle('hidden', state !== 'no');
      if (unknown) unknown.classList.toggle('hidden', state !== 'unknown');
      if (payload) {
        Array.prototype.forEach.call(fc.querySelectorAll('[data-sk-vehicle-label]'), function (el) {
          el.textContent = vehicleLabel(payload.vehicle);
        });
        var cta = fc.querySelector('[data-sk-fit-cta]');
        if (cta) cta.setAttribute('href', resolveVehicleUrl(payload.vehicle) || '/collections/custom-seat-covers');
      }
    });
  }

  function hydrateShopButtons(payload) {
    var buttons = document.querySelectorAll('[data-shop-vehicle]');
    Array.prototype.forEach.call(buttons, function (btn) {
      if (!payload) {
        btn.classList.add('hidden');
        return;
      }
      var url = resolveVehicleUrl(payload.vehicle) || '/collections/custom-seat-covers';
      btn.setAttribute('href', url);
      btn.classList.remove('hidden');
    });
  }

  function hydrate() {
    var payload = readPayload();
    hydrateCards(payload);
    hydrateBars(payload);
    hydrateFitChecks(payload);
    hydrateShopButtons(payload);
  }

  function clearVehicle() {
    try {
      localStorage.removeItem(VEHICLE_KEY);
      localStorage.removeItem(FORM_KEY);
    } catch (e) { /* storage unavailable */ }
    document.dispatchEvent(new CustomEvent('vehicle-cleared', { bubbles: true }));
  }

  window.SKVehicle = {
    get: function () { var p = readPayload(); return p ? p.vehicle : null; },
    payload: readPayload,
    label: function () { var p = readPayload(); return p ? vehicleLabel(p.vehicle) : ''; },
    fits: function (handle) { return productFits(readPayload(), handle); },
    url: function () { var p = readPayload(); return p ? resolveVehicleUrl(p.vehicle) : null; },
    clear: clearVehicle,
    refresh: hydrate
  };

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-sk-vehicle-change]');
    if (trigger) {
      e.preventDefault();
      clearVehicle();
    }
  });

  document.addEventListener('vehicle-found', hydrate);
  document.addEventListener('vehicle-cleared', hydrate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate);
  } else {
    hydrate();
  }
})();
